// Shared helpers for the app's email-sending flows.
//
// Two problems these solve consistently everywhere we send email:
//   1. The browser's <input type="email"> treats dotless domains like
//      "name@gmail" as valid, but the email provider (Resend) rejects them
//      with a 422 — so we guard with a stricter check before the round-trip.
//   2. supabase-js throws a FunctionsHttpError whose message is a generic
//      "Edge Function returned a non-2xx status code". The useful detail (the
//      provider's validation message, "Unauthorized", etc.) lives in the
//      unread Response on `error.context`. We surface it so the user sees why
//      a send actually failed instead of a cryptic status code.

/** True when the value looks like a real address with a dotted domain. */
export const isValidEmail = (value: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value ?? '').trim());

/**
 * Turn a supabase functions error (or any thrown error) into a human-readable
 * Error, reading the edge function's JSON body from `error.context` when
 * present. Known provider validation errors are mapped to plain English.
 */
export async function readFunctionError(
    fnError: any,
    fallback = 'Failed to send email',
): Promise<Error> {
    // Some callers pass `{ data, error }` where the function returned a 2xx but
    // an { error } body; handle a plain string/error too.
    if (typeof fnError === 'string') return new Error(fnError);

    const ctx = fnError?.context;
    if (ctx && typeof ctx.json === 'function') {
        try {
            const body = await ctx.json();
            const raw = typeof body?.error === 'string' ? body.error : '';
            if (raw) return new Error(friendlyMessage(raw));
        } catch {
            // body wasn't JSON / already consumed — fall through
        }
    }

    return new Error(friendlyMessage(fnError?.message) || fallback);
}

/** Map noisy provider/edge errors to something a workshop owner understands. */
function friendlyMessage(raw?: string): string {
    if (!raw) return '';
    if (/invalid\s+`?to`?\s+field|validation_error|invalid.*email address/i.test(raw)) {
        return 'The recipient email address was rejected as invalid. Please check it and try again.';
    }
    if (/unauthorized|forbidden/i.test(raw)) {
        return "You don't have permission to send this email. Try refreshing and signing in again.";
    }
    return raw;
}
